// Leaflet measures its container once, at init, and after
// that only re-measures on a WINDOW resize
// (`trackResize`). A container that gains size on its own
// never triggers that, so the map keeps painting at
// whatever size it was born with: a card revealed from
// `hidden`, a sheet opening, an audit stage flipping
// active (CAL-445).
//
// Three controllers mount Leaflet, so the mechanism lives
// here while each keeps its own policy for what to
// redraw: `location-map` re-runs its draw, `courier-map`
// re-fits its markers, `locating-map` re-fits the
// neighbours it has revealed so far. The watcher only
// says WHEN.

export function containerSize(element) {
  // The same box Leaflet measures in `map.getSize()`, so
  // this reads exactly the quantity the map cares about.
  return {
    width: element.clientWidth,
    height: element.clientHeight,
  };
}

export function containerHasSize(element) {
  const { width, height } = containerSize(element);

  return width > 0 && height > 0;
}

/**
 * Calls `onGainedSize` whenever `element`'s box becomes a
 * NEW non-zero size. Never on the observer's own first
 * delivery, and never while the element is hidden.
 *
 * Returns the observer (call `.disconnect()` in teardown),
 * or null where `ResizeObserver` does not exist (jsdom,
 * and browsers older than Safari 13.1). There the map
 * behaves exactly as it did before.
 */
export function watchContainerSize(element, onGainedSize) {
  if (typeof ResizeObserver === "undefined") return null;

  // Seeded synchronously rather than from the observer's
  // first delivery: an element with no box is not
  // guaranteed to get that callback, and the reveal has
  // to read as a CHANGE against something.
  let previous = containerSize(element);

  const observer = new ResizeObserver(() => {
    const size = containerSize(element);
    const last = previous;

    previous = size;

    // Hidden again: nothing to measure against, and
    // handing Leaflet a 0x0 viewport is what caused this.
    if (!size.width || !size.height) return;

    // The first delivery reports the box the map was
    // already built on. Only a real change is worth a
    // redraw; firing on every tick fights Leaflet's own
    // animations.
    if (
      last.width === size.width &&
      last.height === size.height
    ) {
      return;
    }

    onGainedSize();
  });

  observer.observe(element);

  return observer;
}

/**
 * Leaflet stamps `_leaflet_id` on its container and
 * appends its own top-level nodes to it (the map pane and
 * the control container), and it refuses to build a
 * second map on a stamped container. `map.remove()` is
 * the happy path, but it throws when the container no
 * longer carries the id it stamped, and it throws BEFORE
 * doing any cleanup, so teardown needs this to finish
 * the job (CAL-401).
 *
 * Every node Leaflet generates carries a `leaflet-`
 * prefixed class, so the sweep is scoped to its own DOM:
 * a re-init must never eat a server-rendered child.
 */
export function clearLeafletArtifacts(element) {
  delete element._leaflet_id;

  Array.from(element.children).forEach((child) => {
    const generatedByLeaflet = Array.from(
      child.classList
    ).some((name) => name.startsWith("leaflet-"));

    if (generatedByLeaflet) {
      child.remove();
    }
  });
}
